import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, ClockAlert, Copy, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * REENVIO DE CONTRATO É URGENTE (Douglas 30/09/2026).
 *
 *  · BKO contrato — card BRANCO com a faixa bordô, o relógio de 10 minutos, o
 *    CNPJ para copiar e o botão Reenviado. Não tem X: fica até ele apertar. Os
 *    que chegam enquanto isso esperam atrás ("+N na fila"), por ordem de chegada.
 *  · Consultor dono — card AMARELO "Contrato reenviado" quando o BKO aperta.
 *  · Supervisora (Lisiane) — card AMARELO "Reenvio vencido" no minuto em que
 *    os 10 minutos estouram; some quando o BKO reenvia.
 *
 * ⚠️ O amarelo é exceção autorizada pelo Douglas (30/09/2026) à paleta sem
 * âmbar: vale só para estes dois cards.
 */
export const ORIGENS_CONTRATO = ['contrato_urgente', 'contrato_reenviado', 'contrato_vencido'] as const;

export interface AvisoContrato {
  id: string;
  titulo: string;
  mensagem: string;
  created_at?: string;
  data_inicio?: string;
  metadados?: {
    origem?: string;
    cnpj?: string | null;
    empresa?: string | null;
    pedido_numero?: string | null;
    fala?: string | null;
    vence_em?: string | null;
    bko_nome?: string | null;
    hora?: string | null;
    minutos?: number | null;
    no_prazo?: boolean | null;
    cta_path_confirm?: string | null;
  } | null;
}

const API_BASE = () =>
  (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

const primeiro = (s?: string | null) => String(s || '').split(' ')[0];

function formatarCnpj(c?: string | null): string {
  const d = String(c || '').replace(/\D/g, '');
  return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : d;
}

function relogio(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function atrasoTexto(ms: number): string {
  const min = Math.max(1, Math.floor(ms / 60_000));
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)} h`;
}

async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    const t = document.createElement('textarea');
    t.value = texto;
    t.style.position = 'fixed';
    t.style.opacity = '0';
    document.body.appendChild(t);
    t.select();
    const ok = document.execCommand('copy');
    t.remove();
    return ok;
  }
}

function useAgora(ativo: boolean) {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!ativo) return;
    const id = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [ativo]);
  return agora;
}

function CardBko({ fila, onFeito }: { fila: AvisoContrato[]; onFeito: () => void }) {
  const a = fila[0];
  const m = a.metadados || {};
  const agora = useAgora(true);
  const [copiado, setCopiado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const vence = Date.parse(m.vence_em || '') || agora;
  const restante = vence - agora;
  const vencida = restante <= 0;
  const pct = Math.max(0, Math.min(100, (restante / (10 * 60_000)) * 100));

  const reenviado = async () => {
    setEnviando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`${API_BASE()}/v1/tarefas/contrato-urgente/${a.id}/reenviado`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token ?? ''}`, 'Content-Type': 'application/json' },
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok && r.status !== 404) throw new Error(corpo.error || 'Não foi possível registrar o reenvio.');
      onFeito();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Não foi possível registrar o reenvio.');
    } finally {
      setEnviando(false);
    }
  };

  const Icone = vencida ? ClockAlert : AlertTriangle;
  return (
    <div role="alert" className="w-[340px] overflow-hidden rounded-xl border-[1.5px] border-primary bg-card shadow-lg">
      <div className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-white bg-primary ${vencida ? 'brightness-75' : ''}`}>
        <Icone className="h-3.5 w-3.5" />
        <span className="truncate">{vencida ? `Vencida há ${atrasoTexto(-restante)}` : 'Urgente · Contrato'}</span>
        {fila.length > 1 && (
          <span className="ml-auto flex-shrink-0 rounded bg-white/20 px-1.5 normal-case tracking-normal">+{fila.length - 1} na fila</span>
        )}
      </div>
      <div className="px-3 py-2.5">
        <p className="text-[13px] font-semibold text-foreground">Reenviar aceite ou biometria</p>
        <p className="truncate text-[11px] text-muted-foreground">
          {[m.empresa, m.pedido_numero ? `pedido ${m.pedido_numero}` : null].filter(Boolean).join(' · ')}
        </p>
        {m.cnpj && (
          <button type="button" title="Copiar CNPJ"
            onClick={async () => { if (await copiar(String(m.cnpj).replace(/\D/g, ''))) { setCopiado(true); setTimeout(() => setCopiado(false), 1500); } }}
            className="mt-1.5 inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[12.5px] text-foreground">
            {formatarCnpj(m.cnpj)}
            {copiado ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />}
          </button>
        )}
        {m.fala && <p className="mt-1 truncate text-[10.5px] italic text-muted-foreground">“{m.fala}”</p>}
        {vencida ? (
          <p className="mt-2 text-[11px] text-muted-foreground">A supervisora já sabe. O card fica até você reenviar.</p>
        ) : (
          <>
            <p className="mt-2 font-mono text-[22px] font-semibold leading-none text-primary">{relogio(restante)}</p>
            <div className="mt-1.5 h-1 overflow-hidden rounded bg-muted">
              <div className="h-full bg-primary transition-[width] duration-1000" style={{ width: `${pct}%` }} />
            </div>
          </>
        )}
        <button type="button" disabled={enviando} onClick={reenviado}
          className="mt-2.5 flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-border-warm bg-white text-[12.5px] font-semibold text-foreground hover:bg-muted disabled:opacity-60">
          <Send className="h-3.5 w-3.5" />
          {enviando ? 'Registrando…' : 'Reenviado'}
        </button>
      </div>
    </div>
  );
}

function CardAmarelo({ icone: Icone, faixa, selo, titulo, linhas, botao, onClick }: {
  icone: typeof Send; faixa: string; selo?: string | null; titulo: string; linhas: string[]; botao: string; onClick: () => void;
}) {
  return (
    <div role="status" className="w-[340px] overflow-hidden rounded-xl border-[1.5px] border-amber-600 bg-amber-50 shadow-lg dark:border-amber-500 dark:bg-amber-950">
      <div className="flex items-center gap-1.5 bg-amber-300 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-amber-900 dark:bg-amber-800 dark:text-amber-50">
        <Icone className="h-3.5 w-3.5" />
        <span className="truncate">{faixa}</span>
        {selo && <span className="ml-auto flex-shrink-0 rounded bg-white/70 px-1.5 normal-case tracking-normal dark:bg-black/20">{selo}</span>}
      </div>
      <div className="px-3 py-2.5">
        <p className="truncate text-[13px] font-semibold text-amber-950 dark:text-amber-50">{titulo}</p>
        {linhas.filter(Boolean).map((l, i) => (
          <p key={i} className="text-[11px] text-amber-800 dark:text-amber-200">{l}</p>
        ))}
        <button type="button" onClick={onClick}
          className="mt-2.5 h-[30px] w-full rounded-lg bg-amber-800 text-[12px] font-semibold text-white hover:brightness-110">
          {botao}
        </button>
      </div>
    </div>
  );
}

export default function AvisoContratoUrgente({ avisos, onConfirmar }: {
  avisos: AvisoContrato[];
  onConfirmar: (id: string) => Promise<void>;
}) {
  const navigate = useNavigate();
  const [feitos, setFeitos] = useState<Set<string>>(new Set());
  const agora = useAgora(avisos.some((a) => a.metadados?.origem === 'contrato_vencido'));

  const ordem = (a: AvisoContrato) => Date.parse(a.created_at || a.data_inicio || '') || 0;
  const fila = avisos.filter((a) => a.metadados?.origem === 'contrato_urgente' && !feitos.has(a.id)).sort((x, y) => ordem(x) - ordem(y));
  const reenviados = avisos.filter((a) => a.metadados?.origem === 'contrato_reenviado');
  const vencidos = avisos.filter((a) => a.metadados?.origem === 'contrato_vencido').sort((x, y) => ordem(x) - ordem(y));
  if (!fila.length && !reenviados.length && !vencidos.length) return null;

  const r = reenviados[0];
  const v = vencidos[0];
  const rm = r?.metadados || {};
  const vm = v?.metadados || {};

  return (
    <div className="fixed bottom-24 left-1/2 z-[9500] flex -translate-x-1/2 flex-col gap-2 sm:left-auto sm:right-[380px] sm:translate-x-0">
      {v && (
        <CardAmarelo
          icone={ClockAlert}
          faixa="Reenvio vencido"
          selo={vencidos.length > 1 ? `+${vencidos.length - 1} vencidos` : `há ${atrasoTexto(agora - (Date.parse(vm.vence_em || '') || agora))}`}
          titulo={[primeiro(vm.bko_nome), vm.empresa].filter(Boolean).join(' · ')}
          linhas={[vm.pedido_numero ? `Pedido ${vm.pedido_numero}` : '', 'Some quando o BKO apertar Reenviado.']}
          botao="Ver tarefa"
          onClick={() => navigate(vm.cta_path_confirm || '/tarefas')}
        />
      )}
      {r && (
        <CardAmarelo
          icone={Send}
          faixa="Contrato reenviado"
          selo={reenviados.length > 1 ? `+${reenviados.length - 1}` : null}
          titulo={[rm.empresa, rm.pedido_numero ? `pedido ${rm.pedido_numero}` : null].filter(Boolean).join(' · ')}
          linhas={[
            `${primeiro(rm.bko_nome)} reenviou${rm.hora ? ` às ${rm.hora}` : ''}${rm.minutos ? `, em ${rm.minutos} min` : ''}.`,
            'Confirma com o cliente que o link chegou.',
          ]}
          botao="Acessar tarefa"
          onClick={async () => { await onConfirmar(r.id).catch(() => undefined); navigate(rm.cta_path_confirm || '/whatsapp-inbox?tarefas=1'); }}
        />
      )}
      {fila.length > 0 && (
        <CardBko fila={fila} onFeito={() => setFeitos((s) => new Set(s).add(fila[0].id))} />
      )}
    </div>
  );
}
